import { useEffect, useState } from 'react'
import Icon from '../Icon'
import { del, get, post } from '../../lib/api'
import Tickets from '../Tickets'

/**
 * Maintenance.
 *
 * Migrations, and the ticket queue. Errors live in the Admin console's own
 * Errors tab — core already owns that surface and duplicating it here would
 * give two places to mark the same error resolved.
 */
interface Migrations {
  environment: string
  readable: boolean
  applied: { name: string; applied_at: string }[]
  pending: string[]
  unknown: string[]
  total: number
}

export default function Maintenance() {
  const [data, setData] = useState<Migrations | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void get<Migrations>('/api/admin/migrations')
      .then(setData)
      .catch(() => setError('Could not read the migration state.'))
  }, [])

  return (
    <section>
      <div className="pagehead"><div><p className="eyebrow">Keep it running</p><h1>Maintenance.</h1><p>What the database has applied, and what is still open.</p></div></div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}><Icon name="database" size={18} /> Migrations</h3>
        {error && <p className="error">{error}</p>}
        {!data && !error && <p className="muted">Loading…</p>}

        {data && (
          <>
            <p className="muted small">
              {data.environment} · {data.applied.length} of {data.total} applied
            </p>

            {!data.readable && (
              <p className="muted">No migrations have run in this environment yet.</p>
            )}

            {data.pending.length > 0 && (
              <div className="note-panel">
                <h4>Pending</h4>
                <ul className="small">{data.pending.map((name) => <li key={name}>{name}</li>)}</ul>
                <p className="muted small">
                  Run them from the command line with <code>npm run db:migrate:beta</code>.
                  This screen deliberately cannot run them.
                </p>
              </div>
            )}

            {data.unknown.length > 0 && (
              <div className="note-panel">
                <h4>Applied but not in this build</h4>
                <p className="small">
                  The database is ahead of the code. This environment is probably running an older build.
                </p>
                <ul className="small">{data.unknown.map((name) => <li key={name}>{name}</li>)}</ul>
              </div>
            )}

            {data.pending.length === 0 && data.unknown.length === 0 && data.readable && (
              <p className="muted"><Icon name="check" size={15} /> Up to date.</p>
            )}

            <details>
              <summary>Applied ({data.applied.length})</summary>
              <ul className="small">
                {data.applied.map((row) => (
                  <li key={row.name}>{row.name} <span className="muted">· {row.applied_at}</span></li>
                ))}
              </ul>
            </details>
          </>
        )}
      </div>

      <DemoPanel />

      <ErrorsPanel />

      <div className="card">
        <h3 style={{ marginTop: 0 }}><Icon name="ticket" size={18} /> Tickets</h3>
        <Tickets />
      </div>
    </section>
  )
}


interface DemoCounts { plants: number; species: number; shows: number }

interface ClientError {
  fingerprint: string
  message: string
  source: string | null
  url: string | null
  version: string | null
  commit_sha: string | null
  environment: string | null
  count: number
  first_seen: number
  last_seen: number
  resolved_at: number | null
}

/**
 * Errors & bugs.
 *
 * The same rows the admin console shows, with the one action the console does
 * not have: turn this into a ticket. Everything needed to answer "was it
 * fixed?" — the build, the commit, how often, first and last seen — is carried
 * into the ticket at that moment, because the error row can be resolved and
 * recur while the ticket has to stay true to what was reported.
 *
 * Raising twice is a no-op: the second press hands back the ticket that exists.
 */
function ErrorsPanel() {
  const [errors, setErrors] = useState<ClientError[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [raised, setRaised] = useState<Record<string, string>>({})
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    void get<{ errors: ClientError[] }>('/api/admin/errors')
      .then((r) => setErrors(r.errors))
      .catch(() => setProblem('Could not read the error log.'))
      .finally(() => setLoading(false))
  }, [])

  async function raise(fingerprint: string) {
    setBusy(fingerprint)
    setProblem(null)
    try {
      const { ticket } = await post<{ ticket: { id: string; title: string } }>(
        `/api/admin/errors/${encodeURIComponent(fingerprint)}/ticket`, {},
      )
      setRaised((all) => ({ ...all, [fingerprint]: ticket.id }))
    } catch (failure) {
      setProblem(failure instanceof Error ? failure.message : 'That ticket could not be raised.')
    } finally {
      setBusy(null)
    }
  }

  const when = (ms: number) => new Date(ms).toLocaleString()

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}><Icon name="bug" size={18} /> Errors &amp; bugs</h3>

      {loading && <p className="muted">Loading…</p>}
      {problem && <p className="error">{problem}</p>}

      {!loading && errors.length === 0 && (
        <p className="muted"><Icon name="check" size={15} /> Nothing has gone wrong yet.</p>
      )}

      <ul className="task-list">
        {errors.map((error) => (
          <li key={error.fingerprint}>
            <div style={{ width: '100%' }}>
              <b>{error.message}</b>
              <p className="muted small" style={{ margin: '4px 0' }}>
                {error.source ?? 'unknown'}
                {error.url ? ` · ${error.url}` : ''}
                {' · '}seen {error.count}×
                {' · '}v{error.version ?? '?'} ({error.commit_sha ?? 'unknown commit'})
                {error.environment ? ` · ${error.environment}` : ''}
              </p>
              <p className="muted small" style={{ margin: '0 0 8px' }}>
                First {when(error.first_seen)} · last {when(error.last_seen)}
              </p>
              <div className="row">
                {error.resolved_at
                  ? <span className="chip ok">resolved</span>
                  : <span className="chip warn">open</span>}
                {raised[error.fingerprint]
                  ? <span className="chip">ticket raised</span>
                  : (
                    <button
                      type="button" disabled={busy === error.fingerprint}
                      onClick={() => void raise(error.fingerprint)}
                    >
                      <Icon name="ticket" size={14} />
                      {busy === error.fingerprint ? 'Raising…' : 'Raise a ticket'}
                    </button>
                  )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}


/**
 * Demo stock.
 *
 * Every row it creates is flagged, and Remove deletes on that flag alone — so
 * it stays safe to press after Dillon has catalogued two hundred real plants
 * on top of it. The alternative was somebody reading names and deciding which
 * ones looked made up, which is how real stock gets deleted.
 */
function DemoPanel() {
  const [counts, setCounts] = useState<DemoCounts | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    void get<{ counts: DemoCounts }>('/api/admin/demo').then((r) => setCounts(r.counts)).catch(() => {})
  }, [])

  async function run(action: 'add' | 'remove') {
    setBusy(true); setProblem(null); setNote(null)
    try {
      const result = action === 'add'
        ? await post<{ counts: DemoCounts; note?: string }>('/api/admin/demo', {})
        : await del<{ counts: DemoCounts; note?: string }>('/api/admin/demo')
      setCounts(result.counts)
      if (result.note) setNote(result.note)
    } catch (failure) {
      setProblem(failure instanceof Error ? failure.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const total = (counts?.plants ?? 0) + (counts?.species ?? 0)

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}><Icon name="grid" size={18} /> Demo stock</h3>
      <p className="muted small" style={{ marginTop: 0 }}>
        A sample nursery to look at before there is a real one. Every demo plant is
        tagged <b>DEMO-nn</b> and named <b>SAMPLE</b>, so nothing can be mistaken for stock,
        and real tags still start at PPC-0001.
      </p>

      {counts && (
        <p className="small">
          {total === 0
            ? 'None in this environment.'
            : <>{counts.plants} demo plant{counts.plants === 1 ? '' : 's'} · {counts.species} species</>}
        </p>
      )}

      {problem && <p className="error small">{problem}</p>}
      {note && <p className="muted small">{note}</p>}

      <div className="row">
        <button type="button" onClick={() => void run('add')} disabled={busy}>
          <Icon name="plus" size={14} /> Add demo stock
        </button>
        <button type="button" className="danger" onClick={() => void run('remove')} disabled={busy || total === 0}>
          <Icon name="trash" size={14} /> Remove it all
        </button>
      </div>

      <p className="muted small" style={{ marginBottom: 0 }}>
        Remove deletes only the flagged rows. Anything you or Dillon entered is untouched.
      </p>
    </div>
  )
}
